<template>
  <Dialog :value="show" @close="onClose" :persistent="loading || hasChanges" width="740px">
    <div class="task-edit-modal pa-4">
      <h6 class="task-edit-modal__title">Edit task</h6>

      <div class="task-edit-modal__body">
        <!-- Left: form fields -->
        <div class="task-edit-modal__form">
          <div class="pa-2 mt-3">
            <TipTap
              @input="form.description = $event"
              :value="form.description"
              label="Description"
              :disabled="loading"
              :error="errors.description"
            />
          </div>
          <div class="flex">
            <div class="pa-2 mt-3 xs12">
              <Input
                @input="form.plannedDurationHours = $event"
                :value="form.plannedDurationHours"
                name="plannedDurationHours"
                type="number"
                label="Planned duration in hours"
                class="width100"
                :disabled="loading"
                @keydown.stop
              />
            </div>
          </div>
          <div class="flex">
            <div class="pa-2 mt-3 xs12">
              <DateTimePicker
                @input="form.deadline = $event"
                :value="form.deadline"
                label="Deadline"
                :disabled="loading"
              />
            </div>
          </div>
          <div class="flex">
            <div class="pa-2 mt-3 xs12">
              <Select
                @input="form.boardId = $event"
                :value="form.boardId"
                :items="boardItems"
                label="Board"
                name="boardId"
                class="width100"
                :disabled="loading"
                :error="errors.boardId"
              />
            </div>
          </div>
        </div>

        <!-- Right: comments -->
        <div class="task-edit-modal__comments">
          <TaskComments :taskId="task?.id" />
        </div>
      </div>

      <div class="task-edit-modal__actions flex pt-3">
        <Button variant="text" @click.stop="onClose" :disabled="loading">
          {{ hasChanges ? 'Discard changes' : 'Cancel' }}
        </Button>
        <div class="xs12"></div>
        <Button variant="smooth primary" @click.stop="save" :disabled="loading" :loading="loading">
          Save
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script>
  import Dialog from '../common/Dialog.vue'
  import TipTap from '../common/TipTap.vue'
  import Input from '../common/Input.vue'
  import Select from '../common/Select.vue'
  import DateTimePicker from '../common/DateTimePicker.vue'
  import Button from '../common/Button.vue'
  import TaskComments from './TaskComments.vue'
  import { getFirstLineFromHtml } from '@/utils'

  export default {
    name: 'TaskEditModal',
    components: { Dialog, TipTap, Input, Select, DateTimePicker, Button, TaskComments },
    props: ['show', 'task', 'boards', 'loading'],
    emits: ['close', 'save'],
    data: () => ({
      form: {
        description: '',
        plannedDurationHours: '',
        deadline: '',
        boardId: ''
      },
      errors: {},
      originalForm: {}
    }),
    computed: {
      boardItems() {
        return (this.boards || []).map(b => ({ value: b.id, text: b.title }))
      },
      hasChanges() {
        return JSON.stringify(this.form) !== JSON.stringify(this.originalForm)
      }
    },
    watch: {
      task: {
        immediate: true,
        handler(task) {
          if (task) this.resetForm(task)
        }
      },
      show(val) {
        if (val && this.task) this.resetForm(this.task)
      }
    },
    methods: {
      resetForm(task) {
        const f = {
          description: task.description || '',
          plannedDurationHours: task.plannedDurationHours || '',
          deadline: task.deadline || '',
          boardId: task.boardId || ''
        }
        this.form = { ...f }
        this.originalForm = { ...f }
        this.errors = {}
        // TipTap normalizes HTML on setContent with a 300ms throttle — re-sync
        // originalForm after it settles to avoid a false hasChanges on open
        clearTimeout(this._stabilizeTimeout)
        this._stabilizeTimeout = setTimeout(() => {
          this.originalForm = { ...this.form }
        }, 350)
      },
      validate() {
        const errors = {}
        if (!this.form.description || !getFirstLineFromHtml(this.form.description)?.length) {
          errors.description = 'Must be at least 1 character long'
        }
        if (!this.form.boardId) {
          errors.boardId = 'A board must be selected'
        }
        this.errors = errors
        return !Object.keys(errors).length
      },
      save() {
        if (!this.validate()) return
        this.$emit('save', { ...this.form })
      },
      onClose() {
        this.$emit('close')
      }
    }
  }
</script>

<style scoped>
.task-edit-modal__title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
}

.task-edit-modal__body {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.task-edit-modal__form {
  flex: 1;
  min-width: 0;
}

.task-edit-modal__comments {
  width: 260px;
  flex-shrink: 0;
  padding-top: 12px;
  border-left: 1px solid rgba(0, 0, 0, 0.07);
  padding-left: 20px;
}

.task-edit-modal__actions {
  border-top: 1px solid rgba(0, 0, 0, 0.07);
  margin-top: 8px;
}
</style>
